import React, { forwardRef } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

import classNames from '../../helpers/classNames';
import { useAccommodationFilter } from '../../hooks/useAccommodationFilter';
import useTranslate from '../../hooks/useTranslate';
import getGolfClubInfo from '../../services/apis/golfCourses/getGolfClubInfo';
import { MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import AccommodationAvailability from '../AccommodationAvailability/AccommodationAvailability';
import TeeTimesAvailability from '../TeeTimesAvailability/TeeTimesAvailability';
import styles from './GolfClubAvailability.module.scss';

interface GolfClubAvailabilityProps {
  golfCourse: MappedGolfCourseType;
  selectedDate: string;
  checkinDate: string;
  checkoutDate: string;
  shrinkDatesTable?: boolean;
  onShowMap?: () => void;
  mapVisible?: boolean;
}

const GolfClubAvailability = forwardRef<HTMLDivElement, GolfClubAvailabilityProps>((props, ref) => {
  const {
    golfCourse,
    selectedDate,
    checkinDate,
    checkoutDate,
    shrinkDatesTable,
    onShowMap,
    mapVisible,
  } = props;

  const [isAccommodationFilter, setAccommodationFilter] = useAccommodationFilter();

  const accommodationId = getGolfClubInfo(golfCourse)?.accommodationId ?? null;
  const clubHasAccommodation = !!accommodationId;
  const activeTab = isAccommodationFilter && clubHasAccommodation ? 'accommodations' : 'teetimes';

  const { translate } = useTranslate();

  return (
    <div ref={ref}>
      <Nav className={styles.tabs}>
        <NavItem>
          <NavLink
            active={activeTab === 'teetimes' || !clubHasAccommodation}
            className={classNames(styles.tab, activeTab === 'teetimes' && styles.active)}
            onMouseDown={() => setAccommodationFilter(false)}
          >
            {translate('default.SelectTime.title')}
          </NavLink>
        </NavItem>
        {clubHasAccommodation && (
          <NavItem>
            <NavLink
              active={activeTab === 'accommodations'}
              className={classNames(styles.tab, activeTab === 'accommodations' && styles.active)}
              onMouseDown={() => setAccommodationFilter(true)}
            >
              {translate('GolfCamping.Title')}
            </NavLink>
          </NavItem>
        )}
      </Nav>

      <TabContent activeTab={activeTab} className="mb-4">
        {activeTab === 'teetimes' && (
          <TabPane tabId="teetimes">
            <TeeTimesAvailability
              golfCourse={golfCourse}
              selectedDate={selectedDate}
              shrinkDatesTable={shrinkDatesTable}
              onShowMap={onShowMap}
              mapVisible={mapVisible}
            />
          </TabPane>
        )}
        {activeTab === 'accommodations' && !!accommodationId && (
          <TabPane tabId="accommodations">
            <AccommodationAvailability
              accommodationId={accommodationId}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              onShowMap={onShowMap}
              mapVisible={mapVisible}
            />
          </TabPane>
        )}
      </TabContent>
    </div>
  );
});

export default GolfClubAvailability;
